<script setup>
import { computed, ref, getCurrentInstance } from 'vue'
import { notifyDialog, notifyDialogError } from '../composables/useDialogNotice'
import { DriverStore } from '../pinia/Driver.js'

const CASH_DAILY = 1500000
const SB_DAILY = 5000

const { proxy } = getCurrentInstance()
const Driver = DriverStore()

const currency = ref('d')
const amount = ref('')
const recipient = ref('')
const sending = ref(false)

const recipientRules = [
  v => /^[a-zA-Z0-9]*$/.test(v) || 'Recipient name can only contain letters and numbers'
]

const canSend = computed(() => Number(Driver.persona.level) >= 15 || Number(Driver.persona.prestige) >= 1)
const isCash = computed(() => currency.value === 'd')
const dailyCap = computed(() => isCash.value ? CASH_DAILY : SB_DAILY)
const balance = computed(() => Number(isCash.value ? Driver.persona.cash : Driver.persona.boost) || 0)
const amountHint = computed(() => {
  const daily = isCash.value
    ? `Recipients can receive up to ${formatAmount(CASH_DAILY)} $ per day from other accounts. Resets at midnight.`
    : `Recipients can receive up to ${formatAmount(SB_DAILY)} SB per day from other accounts. Resets at midnight.`
  return daily
})

function formatAmount(value) {
  try {
    if (proxy?.number_format) return proxy.number_format(value)
  } catch {}
  return Intl.NumberFormat('en-US').format(Number(value) || 0)
}

function filterRecipientInput() {
  recipient.value = recipient.value.replace(/[^a-zA-Z0-9]/g, '')
}

async function save() {
  if (!canSend.value) {
    notifyDialog({ type: 'error', message: 'Level 15 is required to send. Prestige 1 also unlocks sending.' })
    return
  }

  const sendAmount = Number(amount.value)
  if (!Number.isFinite(sendAmount) || sendAmount <= 0) {
    notifyDialog({ type: 'error', message: 'Amount must be greater than 0.' })
    return
  }

  if (sendAmount > balance.value) {
    notifyDialog({
      type: 'error',
      message: isCash.value
        ? 'You do not have enough cash for this transfer.'
        : 'You do not have enough Speed Boost for this transfer.',
    })
    return
  }

  if (!recipient.value) {
    notifyDialog({ type: 'error', message: 'Please enter a recipient name.' })
    return
  }

  if (recipientRules[0](recipient.value) !== true) {
    notifyDialog({ type: 'error', message: recipientRules[0](recipient.value) })
    return
  }

  if (String(recipient.value).toLowerCase() === String(Driver.persona.name || '').toLowerCase()) {
    notifyDialog({ type: 'error', message: 'You cannot send money to yourself.' })
    return
  }

  sending.value = true
  try {
    await proxy.$http.post("", JSON.stringify({
      serviceName: "account",
      methodName: "money",
      parameters: [currency.value, sendAmount, recipient.value]
    }))

    if (currency.value == "d")
      Driver.persona.cash = Driver.persona.cash - sendAmount
    else
      Driver.persona.boost = Driver.persona.boost - sendAmount

    const sentUnit = currency.value == "d" ? '$' : 'SB'
    const sentTo = recipient.value
    currency.value = "d"
    amount.value = ''
    recipient.value = ''
    notifyDialog({
      type: 'success',
      message: `Sent ${formatAmount(sendAmount)} ${sentUnit} to ${sentTo}.`,
    })
  } catch (err) {
    notifyDialogError(err, 'Transaction failed')
  } finally {
    sending.value = false
  }
}
</script>

<template><div class="settings-tab-page konopabankForm">
  <div class="settings-tab-header">
    <div class="header-icon">
      <v-icon size="28">mdi-bank</v-icon>
    </div>
    <div class="header-content">
      <h3 class="page-title">Konopa Bank</h3>
      <p class="page-subtitle">Send cash or Speed Boost to another driver. Both of you need level 15 or prestige 1.</p>
    </div>
  </div>

  <v-alert v-if="!canSend" color="warning" icon="mdi-lock" class="mb-4">
    <div class="requirement-notice">
      <div class="notice-title">Account Transfer in Progress</div>
      <div class="notice-text">You are new in the city. Come back when you reach level 15, or prestige 1.</div>
    </div>
  </v-alert>

  <v-form v-if="canSend">
    <div class="section-list">
      <div class="section-row">
        <div class="section-body">
          <div class="currency-selection">
            <button type="button" class="currency-card" :class="{'currency-selected': currency === 'd'}" @click="currency = 'd'">
              <v-icon size="22" class="currency-icon">mdi-currency-usd</v-icon>
              <span class="currency-label">Cash</span>
              <span class="currency-amount">{{ number_format(Driver.persona.cash) }} $</span>
            </button>
            <button type="button" class="currency-card" :class="{'currency-selected': currency === 'b'}" @click="currency = 'b'">
              <v-icon size="22" class="currency-icon">mdi-rocket-launch</v-icon>
              <span class="currency-label">Speed Boost</span>
              <span class="currency-amount">{{ number_format(Driver.persona.boost) }} SB</span>
            </button>
          </div>

          <div class="fields-row">
            <div class="setting-info">
              <div class="setting-label">Amount</div>
              <v-text-field v-model="amount" type="number" min="1" :max="dailyCap" :placeholder="isCash ? '0 $' : '0 SB'" variant="outlined" density="comfortable" hide-details class="setting-input"></v-text-field>
            </div>
            <div class="setting-info">
              <div class="setting-label">Recipient</div>
              <v-text-field v-model="recipient" placeholder="Driver name" variant="outlined" density="comfortable" hide-details :rules="recipientRules" @input="filterRecipientInput" class="setting-input"></v-text-field>
            </div>
          </div>
          <div class="setting-description">{{ amountHint }}</div>

          <v-btn class="save-btn send-btn" variant="elevated" color="primary" size="large" :disabled="sending" @click="save" block>
            <v-icon class="mr-2">mdi-send</v-icon>
            {{ sending ? 'Sending…' : 'Send Transaction' }}
          </v-btn>
        </div>
      </div>
    </div>
  </v-form>
</div></template>

<style scoped>
.currency-selection {
  display:flex;
  flex-direction:column;
  gap:8px}
.currency-card {
  display:flex;
  align-items:center;
  gap:10px;
  width:100%;
  margin:0;
  padding:10px 12px;
  background:var(--glass);
  border:2px solid rgba(255,255,255,.1);
  border-radius:10px;
  cursor:pointer;
  text-align:left;
  color:inherit;
  transition:border-color .15s ease, background .15s ease, box-shadow .15s ease}
.currency-card:hover {
  background:var(--glass-strong);
  border-color:rgba(var(--v-theme-primary),.3)}
.currency-card.currency-selected {
  background:rgba(var(--v-theme-primary),.15);
  border-color:rgb(var(--v-theme-primary));
  box-shadow:0 0 12px rgba(var(--v-theme-primary),.22)}
.currency-icon {
  flex-shrink:0;
  color:rgb(var(--v-theme-primary))}
.currency-label {
  font-size:.88rem;
  font-weight:600;
  color:var(--ink-soft)}
.currency-amount {
  margin-left:auto;
  font-size:.95rem;
  font-weight:700;
  font-variant-numeric:tabular-nums;
  color:var(--ink)}
.fields-row {
  display:grid;
  grid-template-columns:1fr 1fr;
  gap:12px;
  margin-top:16px}
.fields-row .setting-label {
  margin-bottom:8px}
.fields-row + .setting-description {
  margin:8px 0 0}
.requirement-notice {
  text-align:left}
.notice-title {
  font-size:1em;
  font-weight:600;
  margin-bottom:8px}
.notice-text {
  font-size:.9em;
  line-height:1.5}
.send-btn {
  margin-top:14px}
@media (max-width:460px) {
  .fields-row {
    grid-template-columns:1fr}
}
</style>
